/**
 * Hexadecimal Type Detector
 */

import { SEMANTIC_TYPES } from '@/detectors/semanticTypes';
import type { ISemanticType } from '@/interfaces/ISemanticType';
import type { ITypeDetector } from '@/interfaces/ITypeDetector';

export const HexDetector: ITypeDetector = {
  id: 'hex-detector',
  name: 'Hex Detector',
  priority: 45, // Below Base64
  detectedType: SEMANTIC_TYPES.HEX,

  detect(input: string): ISemanticType | null {
    const trimmed = input.trim();
    
    // Must be at least 2 characters
    if (trimmed.length < 2) {
      return null;
    }

    // Check for 0x prefix
    const hasPrefix = trimmed.toLowerCase().startsWith('0x');
    const hexContent = hasPrefix ? trimmed.slice(2) : trimmed;

    // Must be valid hex characters only
    if (!/^[0-9A-Fa-f]+$/.test(hexContent)) {
      return null;
    }

    // If it has 0x prefix, it's definitely hex
    if (hasPrefix && hexContent.length >= 2) {
      return SEMANTIC_TYPES.HEX;
    }

    // Without prefix, need more heuristics
    // Must be even length (bytes are pairs)
    if (hexContent.length % 2 !== 0) {
      return null;
    }

    // Must have some letters to distinguish from plain numbers
    if (!/[A-Fa-f]/.test(hexContent)) {
      // Pure numbers could be anything, need longer length to be confident
      if (hexContent.length < 16) {
        return null;
      }
    }

    // Common hex lengths: 
    // - 32 chars = MD5
    // - 40 chars = SHA1
    // - 64 chars = SHA256
    // - 128 chars = SHA512
    const commonLengths = [32, 40, 64, 128];
    if (commonLengths.includes(hexContent.length)) {
      // These are likely hashes, let Hash detector handle them
      return null;
    }

    // Other even-length hex strings
    if (hexContent.length >= 4) {
      return SEMANTIC_TYPES.HEX;
    }

    return null;
  },
};
